import { TextMessagePartProvider } from "@assistant-ui/react";
import { format, isSameDay } from "date-fns";
import { AlertTriangle, Clock } from "lucide-react";
import { Card, CardContent } from "@repo/ui/components/ui/card";
import { MarkdownText } from "../MarkdownText";
import { conversationSummaryStyles as s } from "./ConversationSummary.styles";
import type { ConversationSummaryData } from "./ConversationSummary.types";
import { EntityAvatar } from "./EntityAvatar";

const FALLBACK_TITLE = "Conversation summary";

function parseDate(value: string): Date | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "Oct 1, 10:04 PM – Oct 2, 6:41 AM", collapsing the date when both ends share a day. */
export function formatSummaryRange(start: string, end: string): string | null {
  const from = parseDate(start);
  const to = parseDate(end);
  if (!from || !to) {
    return null;
  }
  if (from.getTime() === to.getTime()) {
    return format(from, "MMM d, h:mm a");
  }
  if (isSameDay(from, to)) {
    return `${format(from, "MMM d, h:mm a")} – ${format(to, "h:mm a")}`;
  }
  return `${format(from, "MMM d, h:mm a")} – ${format(to, "MMM d, h:mm a")}`;
}

function messageCount(numMessages: number): string {
  return `${numMessages} message${numMessages === 1 ? "" : "s"}`;
}

/**
 * The card as plain text for "Copy message": the header line(s) a reader needs to know what
 * was summarized, then the summary markdown as-is (text parts are copied as raw markdown too).
 * Null when the card would not render.
 */
export function conversationSummaryCopyText({
  title,
  num_messages,
  summary_start_date,
  summary_end_date,
  summary,
}: ConversationSummaryData): string | null {
  if (typeof summary !== "string" || summary.length === 0) {
    return null;
  }
  const range = formatSummaryRange(summary_start_date, summary_end_date);
  const meta = [range, messageCount(num_messages)].filter(Boolean).join(" · ");
  return `${title?.trim() || FALLBACK_TITLE}\n${meta}\n\n${summary}`;
}

export function ConversationSummary({
  title,
  avatar,
  entity_type,
  num_messages,
  summary_start_date,
  summary_end_date,
  summary,
  truncated,
  group_type,
  tag_color,
  is_escalated,
  recipient,
}: ConversationSummaryData) {
  if (typeof summary !== "string" || summary.length === 0) {
    return null;
  }

  const displayTitle = title?.trim() || FALLBACK_TITLE;
  const range = formatSummaryRange(summary_start_date, summary_end_date);
  const count = messageCount(num_messages);

  return (
    <Card className={s.root}>
      <CardContent className={s.header}>
        <div className={s.titleRow}>
          <div className={s.titleGroup}>
            <p className={s.title}>{displayTitle}</p>
            {truncated && (
              <span className={s.partialBadge}>
                <AlertTriangle className={s.partialIcon} aria-hidden />
                Partial history
              </span>
            )}
          </div>
          <EntityAvatar
            entityType={entity_type}
            groupType={group_type}
            title={title}
            avatar={avatar}
            tagColor={tag_color}
            isEscalated={is_escalated}
            recipient={recipient}
          />
        </div>
        <div className={s.metaRow}>
          {range && (
            <p className={s.dates}>
              <Clock className={s.clockIcon} aria-hidden />
              <span className={s.datesText}>{range}</span>
            </p>
          )}
          <span className={s.countPill}>{count}</span>
        </div>
      </CardContent>
      <CardContent className={s.body}>
        <TextMessagePartProvider text={summary}>
          <MarkdownText />
        </TextMessagePartProvider>
      </CardContent>
    </Card>
  );
}
